/**
 * Route guard for external URL redirections.
 * Handles navigation to external websites from Angular routes.
 */

import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot } from '@angular/router';

/**
 * Guard that redirects users to external URLs.
 *
 * @remarks Route guard that performs external redirections by reading
 * the target URL from route data and using window.location.href for navigation.
 * Useful for redirecting to external websites while maintaining route structure.
 *
 */
@Injectable({
  providedIn: 'root',
})
export class RedirectGuard implements CanActivate {
  /**
   * Determines if a route can be activated by redirecting to an external URL.
   *
   * @remarks Reads the external URL from route data and performs a redirect
   * using window.location.href. Always returns true since the redirect happens
   * before the route is technically activated.
   *
   * @param {ActivatedRouteSnapshot} route - The activated route snapshot containing route data
   * @returns {boolean} Always returns true after initiating the redirect
   */
  canActivate(route: ActivatedRouteSnapshot): boolean {
    window.location.href = route.data['externalUrl'];
    return true;
  }
}
